import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Reveal } from "../Reveal";
import { InterestButton } from "../InterestButton";
import { ShareButton } from "../ShareButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const perks = [
  "Lifetime 15% platform rate — locked forever",
  "Free to join, no upfront cost",
  "Priority placement in the marketplace",
];

export const FoundingArtist = React.forwardRef(({ onApply }, ref) => {
  const [form, setForm] = useState({ name: "", artist_name: "", instagram: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    // Load quantity-based scarcity state. Fail silently — the section is still
    // useful without the counter, and we never want to show a broken UI here.
    axios
      .get(`${API}/founding/spots`)
      .then((r) => setSpots(r.data))
      .catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      artist_name: form.artist_name.trim(),
      instagram: form.instagram.trim(),
      email: form.email.trim(),
      consent,
    };
    if (!payload.name || !payload.artist_name || !payload.instagram || !payload.email) {
      toast.error("Please complete every field.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to the privacy terms to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, payload);
      setDone(true);
      toast.success("Application received — welcome to the founding circle.");
      setForm({ name: "", artist_name: "", instagram: "", email: "" });
      setConsent(false);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    backgroundColor: "#0E0A16",
    border: "1px solid #2A2233",
    color: "#F4EEF7",
  };

  return (
    <section
      id="founding"
      ref={ref}
      data-testid="founding-section"
      className="relative py-28 md:py-40 overflow-hidden"
      style={{ backgroundColor: "#120C1D" }}
    >
      {/* Center radial magenta glow at 10% */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(circle at 50% 40%, rgba(199,36,177,0.12) 0%, transparent 55%)" }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        {/* Left: pitch */}
        <Reveal>
          <div>
            <div className="font-inter font-light text-[11px] uppercase tracking-[0.28em] mb-6" style={{ color: "#9B8FAC" }}>
              Founding artists
            </div>
            <h2 className="font-inter font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.15]" style={{ color: "#F4EEF7", letterSpacing: "-0.01em" }}>
              Join first.<br />Keep more, forever.
            </h2>
            <p className="font-inter font-light mt-8 max-w-[44ch] text-base md:text-lg leading-relaxed" style={{ color: "#9B8FAC" }}>
              We're inviting a first circle of artists to shape VYNLWORLD. Lock in a founding rate that never rises — and help set the standard for a fairer merch economy.
            </p>

            {/* Scarcity: quantity-based only. Show the exact remaining count once
                enough spots are taken; otherwise show the "limited to 100" line. */}
            <div
              data-testid="founding-scarcity"
              className="mt-8 pl-4"
              style={{ borderLeft: "2px solid #C724B1" }}
            >
              <p className="text-base md:text-lg leading-relaxed" style={{ color: "#F4EEF7" }}>
                Founding artist spots are limited to{" "}
                <span className="font-display" style={{ color: "#F0C420" }}>100</span>. Lifetime rate, locked forever. When they're gone, they're gone.
              </p>
              {spots?.show_count && (
                <p
                  data-testid="founding-remaining-line"
                  className="mt-3 text-[11px] uppercase tracking-[0.28em]"
                  style={{ color: "#C724B1" }}
                >
                  <span data-testid="founding-remaining-number" className="font-display" style={{ color: "#F4EEF7" }}>
                    {spots.remaining}
                  </span>{" "}
                  of {spots.total} spots remaining
                </p>
              )}
            </div>

            <ul className="mt-10 space-y-4">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-4 text-base" style={{ color: "#F4EEF7" }}>
                  <span className="mt-[10px] w-6 h-px shrink-0" style={{ backgroundColor: "#C724B1" }} />
                  <span>
                    {p.includes("15%") ? (
                      <>
                        Lifetime <span style={{ color: "#F0C420" }} className="font-display">15%</span> platform rate — locked forever
                      </>
                    ) : (
                      p
                    )}
                  </span>
                </li>
              ))}
            </ul>

            {/* Show interest + share */}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <InterestButton onApply={onApply} testid="founding-interest-btn" />
              <ShareButton testid="founding-share-btn" />
            </div>
          </div>
        </Reveal>

        {/* Right: form */}
        <Reveal delay={0.1}>
          <div id="apply-card" data-testid="apply-card" className="p-8 md:p-10" style={{ backgroundColor: "#1A1424", border: "1px solid #2A2233" }}>
            {done ? (
              <motion.div
                data-testid="apply-success"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-10 text-center"
              >
                <div className="font-display text-3xl mb-4" style={{ color: "#F4EEF7" }}>
                  You're on the list.
                </div>
                <p className="text-base leading-relaxed mb-8" style={{ color: "#9B8FAC" }}>
                  We've received your application. Our team will reach out over email with your founding artist details.
                </p>
                <button
                  data-testid="apply-again-btn"
                  onClick={() => setDone(false)}
                  className="btn-sharp text-xs uppercase tracking-[0.22em] px-6 py-3"
                  style={{ border: "1px solid #F4EEF7", color: "#F4EEF7", backgroundColor: "transparent" }}
                >
                  Submit another
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="apply-form" className="space-y-6">
                <div className="font-display text-2xl md:text-3xl mb-2" style={{ color: "#F4EEF7" }}>
                  Apply to found
                </div>
                <p className="text-sm mb-4" style={{ color: "#9B8FAC" }}>
                  Tell us who you are. It takes thirty seconds.
                </p>

                {[
                  { k: "name", label: "Your name", placeholder: "Jane Doe", type: "text" },
                  { k: "artist_name", label: "Artist / brand name", placeholder: "NIGHTFORM", type: "text" },
                  { k: "instagram", label: "Instagram", placeholder: "@yourhandle", type: "text" },
                  { k: "email", label: "Email", placeholder: "you@studio.com", type: "email" },
                ].map((fld) => (
                  <div key={fld.k}>
                    <label htmlFor={`apply-${fld.k}`} className="block text-[11px] uppercase tracking-[0.24em] mb-2" style={{ color: "#9B8FAC" }}>
                      {fld.label}
                    </label>
                    <input
                      id={`apply-${fld.k}`}
                      name={fld.k}
                      data-testid={`apply-${fld.k}`}
                      type={fld.type}
                      value={form[fld.k]}
                      onChange={update(fld.k)}
                      placeholder={fld.placeholder}
                      autoComplete={fld.k === "email" ? "email" : fld.k === "name" ? "name" : "off"}
                      className="w-full px-4 py-3 text-base outline-none transition-colors duration-300 focus:border-[#C724B1]"
                      style={inputStyle}
                    />
                  </div>
                ))}

                <label htmlFor="apply-consent" className="flex items-start gap-3 cursor-pointer group" style={{ color: "#9B8FAC" }}>
                  <input
                    id="apply-consent"
                    name="consent"
                    data-testid="apply-consent"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-[3px] h-4 w-4 shrink-0 appearance-none border cursor-pointer transition-colors duration-200 checked:bg-[#C724B1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#C724B1]"
                    style={{ borderColor: "#2A2233", backgroundColor: consent ? "#C724B1" : "#0E0A16" }}
                  />
                  <span className="text-xs leading-relaxed">
                    I agree to VYNLWORLD storing my details to process my application, as described in the{" "}
                    <a href="/privacy" data-testid="apply-privacy-link" className="underline transition-colors duration-300 hover:text-[#C724B1]" style={{ color: "#F4EEF7" }}>Privacy Notice</a>.
                  </span>
                </label>

                <button
                  data-testid="apply-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-sharp w-full text-xs uppercase tracking-[0.22em] px-8 py-4 font-medium disabled:opacity-60"
                  style={{ backgroundColor: "#C724B1", color: "#F4EEF7" }}
                  onMouseEnter={(e) => !submitting && (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  {submitting ? "Sending…" : "Become a founding artist"}
                </button>
                <p className="text-xs leading-relaxed" style={{ color: "#9B8FAC" }}>
                  By applying you agree to be contacted about the founding programme. Your details are stored securely.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
});

FoundingArtist.displayName = "FoundingArtist";

export default FoundingArtist;
